import React, { useState } from 'react';
import { Grid, Box } from '@mui/material';

import TextField from 'modules/views/shared/TextField';
import Select from 'modules/views/shared/Select';
import AddressAutocomplete from 'modules/views/shared/AddressAutocomplete';
import SkippableFieldIndicator from 'modules/views/shared/SkippableFieldIndicator';
import PiiPinVerificationModals from 'modules/views/shared/PiiPinVerificationModals';
import PiiFieldWrapper from 'modules/views/shared/PiiFieldWrapper';

import { IHomeAddressConfig } from '../hooks/useSectionsConfig';
import { IContactFormState } from '../types';
import { useContactAddressLogic } from '../hooks/useContactAddressLogic';
import { WorkerProfileField } from 'types/profileCustomFields';

interface IHomeAddressSectionProps {
  config: IHomeAddressConfig;
  contactForm: IContactFormState;
  formErrors: Record<string, { message: string }>;
  onFieldChange: (field: WorkerProfileField, value: string) => void;
  onDirectFieldUpdate: (fieldName: keyof IContactFormState, value: string) => void;
  isSubmitting: boolean;
  sectionClasses: string;
  sectionTitleClasses: string;
  labels: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    zipCode: string;
    country: string;
    state: string;
    county: string;
    borough: string;
    homeAddressTitle: string;
  };
  workerProfileFields: {
    homeAddressLine1: WorkerProfileField;
    homeAddressLine2: WorkerProfileField;
    homeAddressCity: WorkerProfileField;
    homeAddressZipPostal: WorkerProfileField;
    homeAddressCountry: WorkerProfileField;
    homeAddressStateProvince: WorkerProfileField;
  };
  isArchivedProject?: boolean;
}

const HomeAddressSection: React.FC<IHomeAddressSectionProps> = ({
  config,
  contactForm,
  formErrors,
  onFieldChange,
  onDirectFieldUpdate,
  isSubmitting,
  sectionClasses,
  sectionTitleClasses,
  labels,
  workerProfileFields,
  isArchivedProject,
}) => {
  // Remove useTranslation - using centralized labels instead

  const {
    countryOptions,
    selectedCountryOption,
    stateOptions,
    selectedStateOption,
    handleCountryChange,
    handleStateChange,
    shouldShowState,
    shouldShowCounty,
    shouldShowBorough,
    handleAddressSelect,
    allowedCountryCodes,
  } = useContactAddressLogic(contactForm, onFieldChange, onDirectFieldUpdate);
  const [showPiiPinVerificationModal, setShowPiiPinVerificationModal] = useState(false);

  if (!config.isVisible) return null;

  return (
    <Grid item xs={12} className={sectionClasses}>
      <p className={sectionTitleClasses}>{labels.homeAddressTitle}</p>
      <Grid container spacing={3}>
        {config.addressLine1.isVisible && (
          <Grid item xs={12}>
            <Box>
              <PiiFieldWrapper
                label={labels.addressLine1}
                required={config.addressLine1.isMandatory}
                piiFieldName="line1"
                onClick={() => setShowPiiPinVerificationModal(true)}
              >
                <AddressAutocomplete
                  label={labels.addressLine1}
                  required={config.addressLine1.isMandatory}
                  testId="addressLine1-input"
                  value={contactForm.addressLine1}
                  error={formErrors.addressLine1?.message || ''}
                  disabled={isSubmitting || isArchivedProject}
                  allowedCountries={allowedCountryCodes}
                  onChange={(address: string) => {
                    if (!isSubmitting) {
                      onFieldChange(workerProfileFields.homeAddressLine1, address);
                      // Clear borough and coordinates when user manually types (not from autocomplete selection)
                      onDirectFieldUpdate('borough', '');
                      onDirectFieldUpdate('latitude', undefined as any);
                      onDirectFieldUpdate('longitude', undefined as any);
                    }
                  }}
                  onAddressSelect={!isSubmitting ? handleAddressSelect : undefined}
                />
              </PiiFieldWrapper>
              <SkippableFieldIndicator show={config.addressLine1.showSkippableIndicator || false} />
            </Box>
          </Grid>
        )}
        {config.addressLine2.isVisible && (
          <Grid item xs={12}>
            <Box>
              <PiiFieldWrapper
                label={labels.addressLine2}
                required={config.addressLine2.isMandatory}
                piiFieldName="line2"
                onClick={() => setShowPiiPinVerificationModal(true)}
              >
                <TextField
                  label={labels.addressLine2}
                  required={config.addressLine2.isMandatory}
                  name="addressLine2"
                  testId="addressLine2-input"
                  value={contactForm.addressLine2}
                  error={formErrors.addressLine2?.message || ''}
                  readOnly={isSubmitting}
                  disabled={isArchivedProject}
                  onChange={
                    !isSubmitting ? e => onFieldChange(workerProfileFields.homeAddressLine2, e.target.value) : undefined
                  }
                />
              </PiiFieldWrapper>
              <SkippableFieldIndicator show={config.addressLine2.showSkippableIndicator || false} />
            </Box>
          </Grid>
        )}
        {config.country.isVisible && (
          <Grid item xs={12}>
            <Box>
              <PiiFieldWrapper
                label={labels.country}
                required={config.country.isMandatory}
                piiFieldName="country"
                onClick={() => setShowPiiPinVerificationModal(true)}
              >
                <Select
                  inputLabel={labels.country}
                  required={config.country.isMandatory}
                  options={countryOptions}
                  selectedValue={selectedCountryOption}
                  error={formErrors.country?.message || ''}
                  testId="country-select"
                  disabled={isSubmitting || isArchivedProject}
                  onChange={!isSubmitting ? handleCountryChange : undefined}
                />
              </PiiFieldWrapper>
              <SkippableFieldIndicator show={config.country.showSkippableIndicator || false} />
            </Box>
          </Grid>
        )}
        {shouldShowState && config.state.isVisible && (
          <Grid item xs={12}>
            <Box>
              <PiiFieldWrapper
                label={labels.state}
                required={config.state.isMandatory}
                piiFieldName="state"
                onClick={() => setShowPiiPinVerificationModal(true)}
              >
                <Select
                  inputLabel={labels.state}
                  required={config.state.isMandatory}
                  options={stateOptions}
                  selectedValue={selectedStateOption}
                  error={formErrors.state?.message || ''}
                  testId="state-select"
                  onChange={!isSubmitting ? handleStateChange : undefined}
                  disabled={!contactForm.country || isSubmitting || isArchivedProject}
                />
              </PiiFieldWrapper>
              <SkippableFieldIndicator show={config.state.showSkippableIndicator || false} />
            </Box>
          </Grid>
        )}
        {config.city.isVisible && (
          <Grid item xs={12}>
            <Box>
              <PiiFieldWrapper
                label={labels.city}
                required={config.city.isMandatory}
                piiFieldName="city"
                onClick={() => setShowPiiPinVerificationModal(true)}
              >
                <TextField
                  label={labels.city}
                  required={config.city.isMandatory}
                  name="city"
                  testId="city-input"
                  value={contactForm.city}
                  error={formErrors.city?.message || ''}
                  readOnly={isSubmitting}
                  disabled={isArchivedProject}
                  onChange={
                    !isSubmitting ? e => onFieldChange(workerProfileFields.homeAddressCity, e.target.value) : undefined
                  }
                />
              </PiiFieldWrapper>
              <SkippableFieldIndicator show={config.city.showSkippableIndicator || false} />
            </Box>
          </Grid>
        )}
        {config.zipCode.isVisible && (
          <Grid item xs={12}>
            <Box>
              <PiiFieldWrapper
                label={labels.zipCode}
                required={config.zipCode.isMandatory}
                piiFieldName="zipCode"
                onClick={() => setShowPiiPinVerificationModal(true)}
              >
                <TextField
                  label={labels.zipCode}
                  required={config.zipCode.isMandatory}
                  name="zipCode"
                  testId="zipCode-input"
                  value={contactForm.zipCode}
                  error={formErrors.zipCode?.message || ''}
                  readOnly={isSubmitting}
                  disabled={isArchivedProject}
                  onChange={
                    !isSubmitting
                      ? e => onFieldChange(workerProfileFields.homeAddressZipPostal, e.target.value)
                      : undefined
                  }
                />
              </PiiFieldWrapper>
              <SkippableFieldIndicator show={config.zipCode.showSkippableIndicator || false} />
            </Box>
          </Grid>
        )}
        {shouldShowCounty && (
          <Grid item xs={12}>
            <Box>
              <PiiFieldWrapper
                label={labels.county}
                piiFieldName="county"
                onClick={() => setShowPiiPinVerificationModal(true)}
              >
                <TextField
                  label={labels.county}
                  name="county"
                  testId="county-input"
                  value={contactForm.county}
                  error={formErrors.county?.message || ''}
                  readOnly={isSubmitting}
                  disabled={isArchivedProject}
                  onChange={!isSubmitting ? e => onDirectFieldUpdate('county', e.target.value) : undefined}
                />
              </PiiFieldWrapper>
            </Box>
          </Grid>
        )}
        {shouldShowBorough && (
          <Grid item xs={12}>
            <Box>
              <PiiFieldWrapper
                label={labels.borough}
                piiFieldName="borough"
                onClick={() => setShowPiiPinVerificationModal(true)}
              >
                <TextField
                  label={labels.borough}
                  name="borough"
                  testId="borough-input"
                  value={contactForm.borough}
                  error={formErrors.borough?.message || ''}
                  readOnly={true}
                  disabled={true}
                />
              </PiiFieldWrapper>
            </Box>
          </Grid>
        )}
      </Grid>
      {showPiiPinVerificationModal && (
        <PiiPinVerificationModals onClose={() => setShowPiiPinVerificationModal(false)} />
      )}
    </Grid>
  );
};

export default HomeAddressSection;
